<template>
  <section class="zhongyi-shell">
    <header class="zhongyi-header">
      <p class="zhongyi-tag">天诚品 · 数据检索工具</p>
      <h1>文献即时检索</h1>
      <p class="zhongyi-subtitle">
        支持按板块名称筛选，实时搜索 <span>文档名</span> 与 <span>文档内容</span> 字段，结果集将展示关键索引。
      </p>
    </header>

    <main class="zhongyi-main">
      <aside class="zhongyi-panel" ref="resultPanel">
        <div class="panel-section">
          <label class="control-label" for="keyword-input">搜索关键字</label>
          <input
            id="keyword-input"
            v-model="keyword"
            @input="scheduleSearch"
            placeholder="请输入关键字（支持 文档名/文档内容"
            autocomplete="off"
          />
        </div>

        <div class="panel-section">
          <label class="control-label" for="fid-select">板块筛选</label>
          <select id="fid-select" v-model="selectedFidname" @change="performSearch">
            <option value="">全部板块</option>
            <option v-for="fid in fidOptions" :key="fid" :value="fid">{{ fid }}</option>
          </select>
        </div>

        <div class="result-meta">
          <strong>{{ results.length }}</strong>
          <span>项匹配</span>
          <span v-if="isSearching">· 正在检索...</span>
          <span v-else-if="searchError">· {{ searchError }}</span>
        </div>

        <div class="result-dropdown">
          <ul class="result-list">
            <li
              v-for="result in results"
              :key="result.id"
              :class="{
                active: selectedResult && selectedResult.id === result.id,
                visited: visitedResultIds.includes(result.id),
              }"
            >
              <button type="button" @click="selectResult(result)">
                <header>
                  <p class="result-subject">{{ result.subject }}</p>
                  <span class="result-fid">{{ result.fidname }}</span>
                </header>
                <p class="result-snippet" v-html="highlightSnippet(result.snippet)"></p>
              </button>
            </li>
            <li v-if="!results.length && !isSearching" class="empty-row">
              暂无匹配结果，请调整关键词或板块
            </li>
          </ul>
          <div class="load-more-bar" v-if="hasMoreResults">
            <button
              type="button"
              class="load-more-button"
              @click="loadMoreResults"
              :disabled="isLoadingMore"
            >
              {{ isLoadingMore ? '加载中...' : '加载更多结果' }}
            </button>
          </div>
        </div>
      </aside>

      <section class="zhongyi-detail">
        <template v-if="selectedResult">
          <div class="detail-card">
            <div class="detail-top">
              <div class="detail-headline">
                <div>
                  <small>{{ selectedResult.fidname }} · {{ selectedResult.uptime }}</small>
                  <h2>{{ selectedResult.subject }}</h2>
                </div>
              </div>
              <div class="detail-meta">
                <span>message 全文长度 {{ selectedResult.message_length }} 字符</span>
                <span v-if="selectedResult.match_count">关键词匹配 {{ selectedResult.match_count }} 处</span>
              </div>
            </div>

            <div class="detail-content">
              <div class="detail-snippets" ref="detailSnippets">
                <div v-if="isLoadingContent" class="empty-row">正在加载全文，请稍候...</div>
                <div v-else-if="detailContent" v-html="highlightContent(detailContent)"></div>
                <div v-else class="empty-row">暂无内容，可点击重新加载</div>
              </div>

              <div class="detail-actions">
                <button type="button" @click="loadResultContent" :disabled="isLoadingContent">
                  {{ isLoadingContent ? '加载中...' : '刷新全文' }}
                </button>
              </div>

              <p v-if="contentError" class="inline-error">{{ contentError }}</p>
            </div>
          </div>
        </template>
        <div v-else class="detail-empty">
          <p>点击左侧列表即可查看全文并自动标红出现的关键词。</p>
          <p>如需要，可点击刷新按钮重新拉取内容。</p>
        </div>
      </section>
    </main>

      <div
        v-if="toolbarVisible"
        class="floating-toolbar"
        :style="floatingToolbarStyle"
        @mousedown.stop.prevent="startToolbarDrag"
      >
        <button
          type="button"
          class="floating-toolbar-button"
          @click="goToPreviousMatch"
          @mousedown.stop
          :disabled="!matchElements.length || currentMatchIndex <= 0"
        >
          上一个
        </button>
        <button
          type="button"
          class="floating-toolbar-button"
          @click="goToNextMatch"
          :disabled="!matchElements.length || currentMatchIndex >= matchElements.length - 1"
          @mousedown.stop
        >
          下一个
        </button>
        <span v-if="matchElements.length" class="floating-match-status">
          {{ currentMatchIndex + 1 }} / {{ matchElements.length }}
        </span>
      </div>
  </section>
</template>

<script>
const API_BASE = (process.env.VUE_APP_API_BASE || '').replace(/\/$/, '')

function buildApiPath(path) {
  return `${API_BASE}${path}`
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export default {
  name: 'ZhongyiSearch',
  data() {
    return {
      keyword: '',
      selectedFidname: '',
      fidOptions: [],
      searchTimer: null,
      results: [],
      pageSize: 40,
      offset: 0,
      hasMoreResults: false,
      isLoadingMore: false,
      selectedResult: null,
      detailContent: '',
      isSearching: false,
      isLoadingContent: false,
      searchError: '',
      contentError: '',
      matchElements: [],
      currentMatchIndex: -1,
      visitedResultIds: [],
      toolbarVisible: false,
      toolbarX: 40,
      toolbarY: 200,
      draggingToolbar: false,
      dragOffsetX: 0,
      dragOffsetY: 0,
    }
  },
  computed: {
    highlightTerms() {
      const words = this.keyword
        .split(/[\s,;；，]+/)
        .map(term => term.trim())
        .filter(Boolean)
      return words.map(escapeRegExp)
    },
    floatingToolbarStyle() {
      return {
        left: `${this.toolbarX}px`,
        top: `${this.toolbarY}px`,
      }
    },
  },
  methods: {
    scheduleSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.performSearch()
      }, 360)
    },
    async fetchFidOptions() {
      try {
        const response = await fetch(buildApiPath('/api/meta.php?type=fidnames'))
        if (!response.ok) {
          throw new Error('无法加载板块列表')
        }
        const payload = await response.json()
        this.fidOptions = payload.fidnames || []
      } catch (error) {
        this.fidOptions = []
        console.warn(error)
      }
    },
    async performSearch() {
      clearTimeout(this.searchTimer)
      if (!this.keyword && !this.selectedFidname) {
        this.results = []
        this.searchError = '请输入关键词或选择板块'
        return
      }
      this.selectedResult = null
      this.detailContent = ''
      this.contentError = ''
      this.matchElements = []
      this.currentMatchIndex = -1
      this.toolbarVisible = false
      this.draggingToolbar = false
      this.results = []
      this.offset = 0
      this.hasMoreResults = false
      this.searchError = ''
      return this.fetchResults()
    },
    loadMoreResults() {
      if (this.isLoadingMore || !this.hasMoreResults) {
        return
      }
      this.fetchResults({ append: true })
    },
    async fetchResults({ append = false } = {}) {
      if (append && (this.isLoadingMore || !this.hasMoreResults)) {
        return
      }
      if (append) {
        this.isLoadingMore = true
      } else {
        this.isSearching = true
        this.searchError = ''
      }
      try {
        const params = new URLSearchParams()
        params.append('keyword', this.keyword)
        if (this.selectedFidname) {
          params.append('fidname', this.selectedFidname)
        }
        params.append('limit', this.pageSize.toString())
        params.append('offset', this.offset.toString())
        const response = await fetch(buildApiPath(`/api/search.php?${params}`))
        if (!response.ok) {
          throw new Error('检索请求失败')
        }
        const payload = await response.json()
        const items = payload.results || []
        this.hasMoreResults = Boolean(payload.has_more)
        if (append) {
          this.results = [...this.results, ...items]
        } else {
          this.results = items
          if (!items.length) {
            this.searchError = '未找到匹配记录'
          }
        }
        this.offset += items.length
      } catch (error) {
        if (!append) {
          this.results = []
          this.searchError = error.message
        }
      } finally {
        if (append) {
          this.isLoadingMore = false
        } else {
          this.isSearching = false
        }
      }
    },
    highlightSnippet(text) {
      if (!text) return ''
      if (!this.highlightTerms.length) {
        return this.escapeHtml(text)
      }
      const regex = new RegExp(`(${this.highlightTerms.join('|')})`, 'gi')
      return this.escapeHtml(text).replace(regex, '<mark>$1</mark>')
    },
    highlightContent(text) {
      if (!text) return ''
      if (!this.highlightTerms.length) {
        return this.escapeHtml(text)
      }
      const regex = new RegExp(`(${this.highlightTerms.join('|')})`, 'gi')
      return this.escapeHtml(text).replace(regex, '<mark>$1</mark>')
    },
    escapeHtml(value) {
      return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    refreshMatchElements() {
      this.$nextTick(() => {
        const container = this.$refs.detailSnippets
        if (!container) {
          this.matchElements = []
          this.currentMatchIndex = -1
          return
        }
        const marks = Array.from(container.querySelectorAll('mark'))
        if (!marks.length && this.highlightTerms.length) {
          setTimeout(this.refreshMatchElements, 50)
          return
        }
        this.matchElements = marks
        if (!marks.length) {
          this.currentMatchIndex = -1
          return
        }
        this.currentMatchIndex = 0
        this.scrollToMatchIndex(0)
      })
    },
    scrollToMatchIndex(index) {
      const container = this.$refs.detailSnippets
      if (!container || !this.matchElements.length) {
        return
      }
      const clamped = Math.max(0, Math.min(index, this.matchElements.length - 1))
      this.currentMatchIndex = clamped
      const target = this.matchElements[clamped]
      if (target.scrollIntoView) {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      } else {
        const offsetTop = target.offsetTop || 0
        container.scrollTop = Math.max(0, offsetTop - 32)
      }
    },
    goToPreviousMatch() {
      if (this.currentMatchIndex > 0) {
        this.scrollToMatchIndex(this.currentMatchIndex - 1)
      }
    },
    goToNextMatch() {
      if (this.currentMatchIndex < this.matchElements.length - 1) {
        this.scrollToMatchIndex(this.currentMatchIndex + 1)
      }
    },
    selectResult(result) {
      if (this.selectedResult && this.selectedResult.id === result.id) {
        return
      }
      this.selectedResult = result
      this.detailContent = ''
      this.contentError = ''
      this.matchElements = []
      this.currentMatchIndex = -1
      if (!this.visitedResultIds.includes(result.id)) {
        this.visitedResultIds = [...this.visitedResultIds, result.id]
      }
      this.loadResultContent()
    },
    async loadResultContent() {
      if (!this.selectedResult) {
        return
      }
      this.isLoadingContent = true
      this.contentError = ''
      this.toolbarVisible = false
      this.matchElements = []
      this.currentMatchIndex = -1
      try {
        const params = new URLSearchParams()
        params.append('id', this.selectedResult.id)
        params.append('full', '1')
        const response = await fetch(buildApiPath(`/api/content.php?${params}`))
        if (!response.ok) {
          throw new Error('加载内容失败')
        }
        const payload = await response.json()
        if (!payload.content) {
          throw new Error('未返回内容')
        }
        this.detailContent = payload.content
        this.refreshMatchElements()
        this.toolbarVisible = true
        this.positionToolbarNearPanel()
      } catch (error) {
        this.contentError = error.message
      } finally {
        this.isLoadingContent = false
      }
    },
    startToolbarDrag(event) {
      if (!this.toolbarVisible) {
        return
      }
      this.draggingToolbar = true
      this.dragOffsetX = event.clientX - this.toolbarX
      this.dragOffsetY = event.clientY - this.toolbarY
    },
    stopToolbarDrag() {
      this.draggingToolbar = false
    },
    onToolbarMouseMove(event) {
      if (!this.draggingToolbar) {
        return
      }
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const minX = 0
      const minY = 0
      const maxX = viewportWidth - 260
      const maxY = viewportHeight - 60
      this.toolbarX = Math.max(minX, Math.min(maxX, event.clientX - this.dragOffsetX))
      this.toolbarY = Math.max(minY, Math.min(maxY, event.clientY - this.dragOffsetY))
    },
    positionToolbarNearPanel() {
      this.$nextTick(() => {
        const panel = this.$refs.resultPanel
        if (!panel) {
          return
        }
        const rect = panel.getBoundingClientRect()
        const margin = 12
        const toolbarWidth = 200
        const left = Math.min(window.innerWidth - toolbarWidth - margin, rect.right + margin)
        const top = Math.max(margin, rect.top + 40)
        this.toolbarX = left
        this.toolbarY = top
      })
    },
  },
  mounted() {
    this.fetchFidOptions()
    window.addEventListener('mousemove', this.onToolbarMouseMove)
    window.addEventListener('mouseup', this.stopToolbarDrag)
  },
  beforeDestroy() {
    window.removeEventListener('mousemove', this.onToolbarMouseMove)
    window.removeEventListener('mouseup', this.stopToolbarDrag)
  },
}
</script>

<style scoped>
.zhongyi-shell {
  padding: 1.5rem 2rem 2rem;
  min-height: calc(100vh - 2rem);
  background: linear-gradient(180deg, #f3f4f6, #ffffff 60%);
}

.zhongyi-header {
  margin-bottom: 1.25rem;
}

.zhongyi-header h1 {
  margin: 0.2rem 0;
  font-size: 2rem;
  color: #1e1e1e;
}

.zhongyi-tag {
  letter-spacing: 0.2em;
  font-size: 0.7rem;
  color: #6b7280;
  text-transform: uppercase;
}

.zhongyi-subtitle {
  margin: 0;
  color: #4b5563;
}

.zhongyi-subtitle span {
  font-weight: 600;
}

.zhongyi-main {
  display: flex;
  gap: 1rem;
}

.zhongyi-panel {
  flex: 0 0 32%;
  background: #fff;
  border-radius: 18px;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  min-height: 70vh;
}

.panel-section {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.9rem;
}

.control-label {
  font-size: 0.85rem;
  color: #4b5563;
  margin-bottom: 0.35rem;
}

.panel-section input,
.panel-section select {
  padding: 0.6rem 0.75rem;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font-size: 0.95rem;
  outline: none;
  transition: border-color 0.2s ease;
}

.panel-section input:focus,
.panel-section select:focus {
  border-color: #6366f1;
}

.result-meta {
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.85rem;
  color: #6b7280;
}

.result-meta strong {
  font-size: 1.2rem;
}

.result-dropdown {
  flex: 1;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  box-shadow: inset 0 0 0 1px rgba(99, 102, 241, 0.06);
  overflow: hidden;
}

.result-list {
  flex: 1;
  list-style: none;
  margin: 0;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  overflow-y: auto;
}

.load-more-bar {
  padding: 0.65rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.load-more-button {
  width: 100%;
  border: none;
  border-radius: 12px;
  padding: 0.65rem 0;
  background: #6366f1;
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s ease;
}

.load-more-button:hover:not(:disabled) {
  background: #4f46e5;
}

.load-more-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.result-list li {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid transparent;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.result-list li.active {
  border-color: #6366f1;
  box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.1);
  background: #fee2e2;
  border-color: #f87171;
}

.result-list li.visited:not(.active) {
  background: #4c0519;
  border-color: #4c0519;
}

.result-list li.visited:not(.active) .result-subject,
.result-list li.visited:not(.active) .result-snippet,
.result-list li.visited:not(.active) .result-fid {
  color: #ffe4e6;
}

.result-list button {
  width: 100%;
  background: transparent;
  border: none;
  padding: 0.65rem 0.75rem;
  text-align: left;
  cursor: pointer;
}

.result-list header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.3rem;
  align-items: baseline;
}

.result-subject {
  margin: 0;
  font-weight: 600;
  color: #1f2937;
  font-size: 0.98rem;
}

.result-fid {
  font-size: 0.75rem;
  color: #4b5563;
  padding: 0.15rem 0.5rem;
  border-radius: 20px;
  border: 1px solid #e5e7eb;
}

.result-snippet {
  margin: 0;
  font-size: 0.85rem;
  color: #374151;
  line-height: 1.4;
}

.result-snippet mark {
  background: #fde68a;
  color: #92400e;
  padding: 0 0.1rem;
  border-radius: 2px;
}

.empty-row {
  font-size: 0.85rem;
  color: #9ca3af;
  text-align: center;
  padding: 1rem 0;
}

.zhongyi-detail {
  flex: 1;
  background: #0f172a;
  border-radius: 18px;
  padding: 1.25rem;
  color: #f8fafc;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  position: sticky;
  top: 1.5rem;
  align-self: flex-start;
  max-height: calc(100vh - 3rem);
  overflow: auto;
}

.detail-headline {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
}

.detail-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.detail-buttons {
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
  align-items: center;
}

.detail-header-button {
  border: none;
  border-radius: 8px;
  padding: 0.35rem 0.75rem;
  background: rgba(248, 250, 252, 0.12);
  color: #f8fafc;
  cursor: pointer;
  transition: background 0.2s ease;
  font-size: 0.75rem;
}

.detail-header-button:hover:not(:disabled) {
  background: rgba(248, 250, 252, 0.25);
}

.detail-header-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.floating-match-status {
  color: rgba(248, 250, 252, 0.7);
  font-size: 0.75rem;
  margin-left: 0.35rem;
}

.detail-card {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-top: 1.5rem;
}

.detail-top {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.detail-content {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.floating-toolbar {
  position: fixed;
  z-index: 50;
  width: 180px;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.65rem;
  border-radius: 20px;
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.45);
}

.floating-toolbar-button {
  border: none;
  border-radius: 12px;
  padding: 0.65rem 0.75rem;
  background: rgba(248, 250, 252, 0.12);
  color: #f8fafc;
  font-size: 0.75rem;
  cursor: grab;
  transition: background 0.2s ease;
}

.floating-toolbar-button:hover:not(:disabled) {
  background: rgba(248, 250, 252, 0.25);
}

.floating-toolbar-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.floating-toolbar span {
  color: rgba(248, 250, 252, 0.7);
  font-size: 0.75rem;
  text-align: center;
}

.detail-card h2 {
  margin: 0.2rem 0 0;
  font-size: 1.6rem;
}

.detail-meta {
  text-align: right;
  font-size: 0.8rem;
  color: #9ca3af;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.detail-snippets {
  margin-top: 1rem;
  flex: 1;
  overflow-y: auto;
  padding-right: 0.5rem;
}

.detail-snippets > div {
  line-height: 1.6;
  margin-bottom: 1rem;
  white-space: pre-wrap;
  word-break: break-word;
  padding-bottom: 0.5rem;
}

.detail-snippets mark {
  background: #f87171;
  color: #1f1f1f;
  padding: 0 0.05rem;
  border-radius: 2px;
}

.detail-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.detail-actions button {
  flex: 1;
  padding: 0.65rem 0.8rem;
  border-radius: 12px;
  border: none;
  background: #6366f1;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}

.detail-actions button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.detail-empty {
  text-align: center;
  color: rgba(248, 250, 252, 0.8);
  font-size: 0.95rem;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.5rem;
  flex: 1;
}

.inline-error {
  color: #f87171;
  margin-top: 0.5rem;
}

@media (max-width: 1100px) {
  .zhongyi-main {
    flex-direction: column;
  }

  .zhongyi-panel,
  .zhongyi-detail {
    flex: 1 1 auto;
  }

  .zhongyi-detail {
    position: static;
    top: auto;
    align-self: auto;
    max-height: none;
    overflow: visible;
  }

  .detail-card {
    height: auto;
  }

  .detail-empty {
    height: auto;
  }
}
</style>
